/**
 * line_item_photo_controller.js: the photo a line item carries
 * (LineItemMixin.attachment_id), on a row of the shared line editor.
 * static/js/utils/line-items-manager.js renders the mount (_renderPhotoMount)
 * and stores what this announces (_bindPhoto).
 *
 * A line takes one of its own record's photos, which the client sees under the
 * line; the server refuses any other (BaseLineItemService._validated_photo).
 * The picker offers the record's gallery, or a new photo, which is uploaded to
 * the record first so it is one of its photos. Both need the network, so with
 * none the picker does not open.
 *
 * On a create form (holds) there is no record yet: the camera button opens the
 * line's own file input, which posts with the form as line_item_photo_<key>,
 * and the row names it held-photo:<key>. The server stores it on the new
 * record (application/web/views/quote_create_form.py rows_with_held_photos).
 *
 * Announces line-item-photo:change with { attachmentId }, null when cleared.
 */
import { Controller } from "@hotwired/stimulus";
import { shrinkPhoto } from "./_photo_shrink.js";

// Each control needs its own input id for its <label for>, and on a create
// form its own key.
let mounted = 0;

const ADD_LABEL = 'title="Add a photo to this line" aria-label="Add a photo to this line"';

// One input with no `capture`: phones offer camera or library from it, and
// iOS shows its own sheet whatever the attributes. A <label for> rather than a
// script click, which some WebViews ignore for file inputs.
function markup(inputId, holds) {
    const add = holds
        ? `<label class="btn btn-sm line-item__photo-add" for="${inputId}" ${ADD_LABEL}>
               <i class="bi bi-camera"></i>
           </label>`
        : `<button type="button" class="btn btn-sm line-item__photo-add"
                   data-action="line-item-photo#toggle" ${ADD_LABEL}>
               <i class="bi bi-camera"></i>
           </button>`;
    return `
        ${add}
        <span class="line-item__photo-chosen" hidden>
            <img alt="Photo on this line">
            <button type="button" class="line-item__photo-clear" data-action="line-item-photo#clear"
                    title="Take the photo off this line" aria-label="Take the photo off this line">
                <i class="bi bi-x"></i>
            </button>
        </span>
        <div class="line-item__photo-picker" hidden>
            <div class="line-item__photo-gallery"></div>
            <label class="btn btn-sm btn-outline-secondary" for="${inputId}">
                <i class="bi bi-plus-lg me-1"></i>New photo
            </label>
        </div>
        <input type="file" accept="image/*" class="d-none" id="${inputId}"
               data-action="change->line-item-photo#upload">`;
}

function csrfHeaders() {
    const token = (typeof FormValidator !== 'undefined' && FormValidator.getCsrfToken)
        ? FormValidator.getCsrfToken() : null;
    return token ? { 'X-CSRFToken': token } : {};
}

function notify(message, level) {
    if (typeof window.showNotification === 'function') window.showNotification(message, level);
}

export default class extends Controller {
    static values = { entityType: String, entityId: String, attachmentId: String, holds: Boolean };

    connect() {
        this.key = String(++mounted);
        this.element.innerHTML = markup(`line-item-photo-${this.key}`, this.holdsValue);
        this.input = this.element.querySelector('input[type="file"]');
        if (this.holdsValue) this.input.name = `line_item_photo_${this.key}`;
        this._showChosen();
    }

    disconnect() {
        this._releasePreview();
    }

    async toggle() {
        const picker = this._picker();
        if (!picker.hidden) {
            picker.hidden = true;
            return;
        }
        if (!navigator.onLine) {
            notify("A photo can be added to a line once you're back online.", 'error');
            return;
        }
        picker.hidden = false;
        await this._loadGallery();
    }

    choose(event) {
        this._set(event.currentTarget.dataset.attachmentId);
    }

    clear() {
        if (this.holdsValue) this.input.value = '';
        this._releasePreview();
        this._set('');
    }

    async upload(event) {
        const file = event.target.files && event.target.files[0];
        if (this.holdsValue) {
            this._hold(file);
            return;
        }
        event.target.value = '';   // the same file can be picked again later
        if (!file) return;
        try {
            this._set(String((await this._post(file)).id));
        } catch (err) {
            notify(err.message || 'Upload failed', 'error');
        }
    }

    _picker() {
        return this.element.querySelector('.line-item__photo-picker');
    }

    // The file stays in the input, which posts it with the form; the row
    // names it by this control's key. Shown from the device until then.
    _hold(file) {
        this._releasePreview();
        if (!file) {
            this._set('');
            return;
        }
        this._preview = URL.createObjectURL(file);
        this._set(`held-photo:${this.key}`);
        this._shrinkHeld(file);
    }

    // A smaller copy takes the original's place in the input once ready (the
    // whole form is one request under nginx's cap); a save tapped before
    // then posts the original.
    async _shrinkHeld(file) {
        const small = await shrinkPhoto(file);
        if (small === file || !this.input.files || this.input.files[0] !== file) return;
        try {
            const transfer = new DataTransfer();
            transfer.items.add(small);
            this.input.files = transfer.files;
        } catch {
            // No DataTransfer constructor: the original stays in the input.
        }
    }

    _releasePreview() {
        if (this._preview) URL.revokeObjectURL(this._preview);
        this._preview = null;
    }

    _set(id) {
        this.attachmentIdValue = id;
        this._picker().hidden = true;
        this._showChosen();
        this.dispatch('change', { detail: { attachmentId: id || null } });
    }

    _showChosen() {
        const id = this.attachmentIdValue;
        const chosen = this.element.querySelector('.line-item__photo-chosen');
        chosen.hidden = !id;
        this.element.querySelector('.line-item__photo-add').hidden = !!id;
        if (id) chosen.querySelector('img').src = this._preview || `/api/v1/attachments/${id}/file?size=thumb`;
    }

    // Fetched on every open: the Photos card can have gained or lost photos
    // since the editor opened.
    async _loadGallery() {
        const gallery = this.element.querySelector('.line-item__photo-gallery');
        gallery.textContent = 'Loading photos…';
        const params = new URLSearchParams({
            entity_type: this.entityTypeValue, entity_id: this.entityIdValue, kind: 'image',
        });
        try {
            const resp = await fetch(`/api/v1/attachments?${params}`, { credentials: 'same-origin' });
            if (!resp.ok) throw new Error('Could not load photos');
            this._renderGallery(gallery, await resp.json());
        } catch (err) {
            gallery.textContent = err.message;
        }
    }

    _renderGallery(gallery, rows) {
        if (!rows.length) {
            gallery.textContent = `No photos on this ${this.entityTypeValue} yet.`;
            return;
        }
        gallery.replaceChildren(...rows.map((row) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'line-item__photo-thumb';
            btn.title = row.caption || row.original_filename || '';
            btn.dataset.attachmentId = String(row.id);
            btn.dataset.action = 'line-item-photo#choose';
            const img = document.createElement('img');
            img.src = row.thumb_url;
            img.alt = '';
            img.loading = 'lazy';
            btn.appendChild(img);
            return btn;
        }));
    }

    async _post(file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', this.entityTypeValue);
        fd.append('entity_id', this.entityIdValue);
        fd.append('kind', 'image');
        const resp = await fetch('/api/v1/attachments', {
            method: 'POST', body: fd, headers: csrfHeaders(), credentials: 'same-origin',
        });
        if (!resp.ok) {
            let msg = 'Upload failed';
            try { msg = (await resp.json()).error || msg; } catch (_e) { /* keep the default */ }
            throw new Error(msg);
        }
        return resp.json();
    }
}
